import { useEffect, useRef, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { LogOut, Menu } from "lucide-react";
import { useAuthStore } from "@/stores/authStore";
import Sidebar from "@/components/Sidebar";
import MobileNavDrawer from "@/components/MobileNavDrawer";
import ScrollNav from "@/components/ScrollNav";
import TableInteract from "@/components/TableInteract";
import EnvSwitcher from "@/components/EnvSwitcher";
import { useIsMobile } from "@/hooks/useMediaQuery";

// 표의 아무 셀이나 더블클릭하면 그 값이 복사된다(2026-07-18).
function CellCopyToast() {
  const [msg, setMsg] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    async function onDblClick(e) {
      if (e.target.closest("input, textarea, select, button, a")) return;
      const cell = e.target.closest("td, th");
      if (!cell || !cell.closest("table")) return;
      const text = cell.innerText.trim();
      if (!text) return;
      try {
        await navigator.clipboard.writeText(text);
        setMsg(`"${text.length > 24 ? text.slice(0, 24) + "…" : text}" 복사됨`);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setMsg(null), 1400);
      } catch {
        // 클립보드 권한 거부
      }
    }
    document.addEventListener("dblclick", onDblClick);
    return () => {
      document.removeEventListener("dblclick", onDblClick);
      clearTimeout(timer.current);
    };
  }, []);

  if (!msg) return null;
  return (
    <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-full bg-ink-900 px-4 py-2 text-xs font-medium text-white shadow-lg">
      {msg}
    </div>
  );
}

// Layout — Sidebar 기반 Backoffice (Phase 2-A, 2026-08-18).
export default function Layout() {
  const navigate = useNavigate();
  const signOut = useAuthStore((s) => s.signOut);
  const email = useAuthStore((s) => s.email);
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);

  function handleSignOut() {
    signOut();
    navigate("/login", { replace: true });
  }

  return (
    <div className="flex h-screen overflow-hidden bg-ink-50">
      {/* 데스크톱(md+) 전용 고정 Sidebar — 모바일에서는 숨기고 Drawer 로 대체.
          hidden md:flex 로 DOM 은 남기되 모바일 폭 점유 0. desktop 레이아웃 무변경. */}
      <div className="hidden md:flex">
        <Sidebar />
      </div>

      {/* 모바일 전용 Drawer (< md). GROUPS/SECTIONS 재사용 — IA 이중 관리 없음. */}
      {isMobile && <MobileNavDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />}

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-14 shrink-0 items-center justify-between gap-3 border-b border-ink-100 bg-white px-4 sm:px-6"
                style={{ paddingTop: "env(safe-area-inset-top)" }}>
          <div className="flex min-w-0 items-center gap-2">
            {/* 모바일 전용 햄버거 — 데스크톱은 숨김(md:hidden). */}
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              aria-label="메뉴 열기"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-ink-600 hover:bg-ink-100 md:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
            {/* 모바일 타이틀(데스크톱은 Sidebar 로고가 있으니 숨김) */}
            <span className="truncate text-[15px] font-semibold text-ink-900 md:hidden">
              Nova <span className="font-normal text-ink-400">Backoffice</span>
            </span>
            {/* 데스크톱은 좌측에 EnvSwitcher(기존 위치) */}
            <div className="hidden md:block">
              <EnvSwitcher />
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            {/* 모바일은 EnvSwitcher 를 우측으로 (좁아서 배지+토글만) */}
            <div className="md:hidden">
              <EnvSwitcher />
            </div>
            {email && <span className="hidden text-xs text-ink-400 lg:inline">{email}</span>}
            <button
              onClick={handleSignOut}
              className="flex items-center gap-1 rounded-md border border-ink-200 px-2 py-1 text-sm text-ink-600 hover:bg-ink-100"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">로그아웃</span>
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto px-3 py-4 sm:px-6 sm:py-6">
          <Outlet />
        </main>
      </div>

      <ScrollNav />
      <CellCopyToast />
      <TableInteract />
    </div>
  );
}
